import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BodyWrapper, Header, Loader, MainWrapper, Message } from '@components';
import { errorsFromMutate } from '@Forms/utils.js';
import { useDatamodel } from '@hooks';
import {
  authorsQuery,
  blogCategoriesQuery,
  businessQuery,
  postMutation,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import PostForm from '../../Site/components/PostForm/PostForm.jsx';
import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

export const TemplateAddPost = () => {
  const { t } = useTranslation();
  const { businessID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);

  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errorMessage]);

  const { data: businessData } = useQuery(businessQuery(businessID));

  const { data: siteData } = useQuery(siteQuery(siteID));

  const { data: authorsData, isPending: authorsIsPending } = useQuery(
    authorsQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  const { data: blogCategoriesData, isPending: blogCategoriesIsPending } =
    useQuery(
      blogCategoriesQuery({
        limit: 100,
        site_id: siteID,
      }),
    );

  const {
    datamodelData,
    isPending: isCustomBlogsPending,
    relatedDatamodels,
    relatedDatamodelsReady,
  } = useDatamodel('blog_custom_fields');

  const mutation = useMutation(
    postMutation(null, {
      onMutate: () => {
        setErrorMessage(null);
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        navigate(
          `/dashboard/projects/${businessID}/templates/${siteID}/blogs/posts/${data.id}`,
        );
      },
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if (allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  const defaultPost = {
    published: true,
    site_id: siteID,
  };

  const blogsPath = `/dashboard/projects/${businessID}/templates/${siteID}/blogs`;

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <Link to={blogsPath}>{t('Blog posts')}</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Add')}</span>
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message
                  closable={true}
                  messageStyle="error"
                  title={'Something Went Wrong'}
                  isFormMessage={false}
                  message={errorMessage}
                />
              </div>
            )}
            {(authorsIsPending ||
              blogCategoriesIsPending ||
              isCustomBlogsPending ||
              !relatedDatamodelsReady) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {authorsData && blogCategoriesData && !isCustomBlogsPending && (
              <PostForm
                postData={defaultPost}
                authorsData={authorsData}
                categoriesData={blogCategoriesData}
                datamodelData={datamodelData}
                relatedDatamodels={relatedDatamodels}
                mutation={mutation}
                disabled={mutation.isPending}
                siteData={siteData}
                onCancel={() => {
                  navigate(blogsPath);
                }}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
